import { tv } from '../../lib/tv';

/** Figma Day Cell "State" (62:33), in code casing. */
export type DayCellState =
  | 'default'
  | 'today'
  | 'selected'
  | 'range-start'
  | 'in-range'
  | 'range-end'
  | 'disabled'
  | 'outside';

const SELECTED = 'text-label-md bg-brand-strong text-text-inverse';
const HOVER = 'group-hover/day:bg-bg-muted';

/**
 * Figma Day Cell (62:33): a 40×40 hit area (the button) around a 36px day
 * chip, radius/md. Selected / range ends = brand/strong chip + text/inverse
 * Label/Medium; Today = 1.5px border/brand inside stroke + text/brand;
 * the range band is a brand/subtle strip 36 tall behind the chips (right
 * half on the start, full width in range, left half on the end). Hover =
 * bg/muted chip. Disabled adds a strikethrough, as every disabled day in the
 * Date Picker (62:342) does - the Day Cell master has none.
 */
export const dayCellStyles = tv({
  slots: {
    root: 'group/day relative flex size-10 shrink-0 cursor-pointer items-center justify-center outline-none select-none disabled:cursor-not-allowed aria-disabled:cursor-not-allowed',
    band: 'absolute inset-y-0.5 hidden bg-brand-subtle',
    chip: [
      'text-body-md text-text-primary relative flex size-9 items-center justify-center rounded-md',
      'transition-colors duration-[120ms] ease-out',
      // No focus state in Figma: the Focus/Ring effect every control uses.
      'group-focus-visible/day:shadow-focus-ring',
    ],
  },
  variants: {
    state: {
      default: { chip: HOVER },
      today: {
        chip: `text-label-md text-text-brand inset-ring-[1.5px] inset-ring-border-brand ${HOVER}`,
      },
      selected: { chip: SELECTED },
      'range-start': { band: 'right-0 left-1/2 block', chip: SELECTED },
      'in-range': { band: 'inset-x-0 block', chip: HOVER },
      'range-end': { band: 'right-1/2 left-0 block', chip: SELECTED },
      disabled: { chip: 'text-text-disabled line-through' },
      outside: { chip: `text-text-tertiary ${HOVER}` },
    },
  },
  defaultVariants: { state: 'default' },
});

/** Selection flags of a day, as react-day-picker reports them. */
export type DayFlags = {
  selected?: boolean;
  range_start?: boolean;
  range_middle?: boolean;
  range_end?: boolean;
  disabled?: boolean;
  outside?: boolean;
  today?: boolean;
};

/** One Figma state per day: disabled, then selection, then today / outside. */
export function dayCellState(m: DayFlags): DayCellState {
  if (m.disabled) return 'disabled';
  if (m.range_start && m.range_end) return 'selected';
  if (m.range_start) return 'range-start';
  if (m.range_end) return 'range-end';
  if (m.range_middle) return 'in-range';
  if (m.selected) return 'selected';
  if (m.outside) return 'outside';
  if (m.today) return 'today';
  return 'default';
}

/**
 * Figma Date Picker surface (62:342): bg/surface, 1px border/subtle,
 * radius/lg, Shadow/MD, 12px padding. Same as the Menu surface except padding.
 */
export const datePickerPanel =
  'bg-bg-surface text-text-primary w-fit rounded-lg border border-border-subtle p-3 shadow-md';
